<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Daily Order Value Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyOrderValueFilter"
              @reset="resetOrderValueFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingOrderValueFilter.startDate"
                  :endDate="editingOrderValueFilter.endDate"
                  @updateStartDate="editingOrderValueFilter.startDate = $event"
                  @updateEndDate="editingOrderValueFilter.endDate = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedOrderValueFilter.startDate | formatDate}} - {{appliedOrderValueFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Total Order Value" highlight>
              {{formatCurrency(orderSummaryData.orderTotalValue)}}
              <template v-slot:info>Over {{orderSummaryData.orderTotalCount}} order{{displayCountPlural(orderSummaryData.orderTotalCount)}} during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Daily Value">
              {{formatCurrency(orderSummaryData.averageOrderValue)}}
              <template v-slot:info>Over {{orderSummaryData.orderTotalCount}} order{{displayCountPlural(orderSummaryData.orderTotalCount)}} during the period containing {{orderSummaryData.activeOrderDayCount}} working day{{displayCountPlural(orderSummaryData.activeOrderDayCount)}}</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow v-if="orderSummaryData.largestOrderInfo">
          <div class="col">
            <AnalyticsDashboardValue title="Largest Order">
              {{formatCurrency(orderSummaryData.largestOrderInfo.totalPrice)}}
              <template v-slot:info>Order <LinkOrder :id="orderSummaryData.largestOrderInfo.id" :name="orderSummaryData.largestOrderInfo.sequenceNumber"></LinkOrder> for {{orderSummaryData.largestOrderInfo.contact.name}} on {{formatDate(orderSummaryData.largestOrderInfo.dateOrderCreated)}}</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Daily Order Value"
          :series="orderDailyValueData.series"
          :xAxis="orderDailyValueData.xAxis"
          :yAxis="orderDailyValueData.yAxis"
          :legend="orderDailyValueData.legend"
          :options="orderDailyValueData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

export default {
  name: 'OrderDailyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      orderSummaryData: {
        orderTotalValue: 0,
        orderTotalCount: 0,
        averageOrderValue: 0,
        largestOrderInfo: null,
        activeOrderDayCount: 0
      },
      orderDailyValueData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultOrderValueFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingOrderValueFilter: {},
      appliedOrderValueFilter: {},
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    }
  },
  mounted() {
    this.editingOrderValueFilter = _.clone(this.defaultOrderValueFilter)
    this.appliedOrderValueFilter = _.clone(this.defaultOrderValueFilter)

    this.requestChartData()

    this.subscribeEndpoint('/orders', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyOrderValueFilter() {
      this.appliedOrderValueFilter = _.clone(this.editingOrderValueFilter)

      this.requestChartData()
    },
    resetOrderValueFilter() {
      this.editingOrderValueFilter = _.clone(this.defaultOrderValueFilter)
      this.appliedOrderValueFilter = _.clone(this.defaultOrderValueFilter)

      this.requestChartData()
    },
    requestChartData() {
      var orderValueFilter = {
        type: 'daily',
        startDate: this.appliedOrderValueFilter.startDate,
        endDate: this.appliedOrderValueFilter.endDate,
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      let widgetComp = this

      Promise.all([
        this.$apiService.analyticsService.getOrdersValueChartData(orderValueFilter),
        this.$apiService.analyticsService.getOrdersValueSummary(orderValueFilter)
      ])
      .then(results => {
        let chartData = results[0].data
        let orderSummaryData = results[1].data

        this.orderDailyValueData.legend = {
          data: ['Order Value', 'Order Count']
        }
        this.orderDailyValueData.xAxis = {
          exportName: 'Date',
          exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
          data: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM'))
        }
        this.orderDailyValueData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return this.formatCurrencySuffix(value, 1);
              }
            }
          },
          { type: 'value', alignTicks: true }
        ]
        this.orderDailyValueData.series = []
        this.orderDailyValueData.series.push({
          type: 'bar',
          name: 'Order Value',
          data: chartData.map(chartEntry => chartEntry.totalOrderValue),
          tooltip: {
            valueFormatter(value) {
              return widgetComp.formatCurrency(value);
            }
          }
        })

        this.orderDailyValueData.series.push({
          type: 'line',
          name: 'Order Count',
          yAxisIndex: 1,
          data: chartData.map(chartEntry => chartEntry.totalOrderCount),
        })

        // Summary info
        this.orderSummaryData.averageOrderValue = 0
        if(chartData.length > 0) {
          this.orderSummaryData.averageOrderValue = chartData.reduce((curValue, orderInfo) => curValue + orderInfo.totalOrderValue, 0) / chartData.length
        }

        this.orderSummaryData.activeOrderDayCount = chartData.filter(orderInfo => orderInfo.totalOrderCount > 0).length
        this.orderSummaryData.orderTotalValue = orderSummaryData.orderTotalValue
        this.orderSummaryData.orderTotalCount = orderSummaryData.orderTotalCount
        this.orderSummaryData.largestOrderInfo = orderSummaryData.largestOrder
      })
    },
  }
}
</script>